import React, { useState, useEffect, useRef } from 'react';
import ibmLogo from '../assets/Ibm.jpg';
import steadyrabbitLogo from '../assets/steadyrabbit.jpeg';
import arawinzLogo from '../assets/arawinz.png';

const Experience = () => {
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [hoveredSkill, setHoveredSkill] = useState(null);
  const cardRefs = useRef([]);
  const [isMobile, setIsMobile] = useState(false);

  const experiences = [
    {
      date: 'Jul 2021 – Jun 2023',
      title: 'Frontend Software Engineer',
      company: 'IBM',
      location: 'Hyderabad, Telangana, India · Hybrid',
      icon: '🔷',
      logo: ibmLogo,
      color: '#0A84FF',
      duration: '2 years',
      highlights: [
        { metric: '60%', label: 'Dev Redundancy Reduced' },
        { metric: '3s', label: 'Page Load (from 8s)' },
        { metric: '3+', label: 'Enterprise Clients' },
        { metric: '62%', label: 'Load Time Reduction' },
        { metric: '45%', label: 'Deployment Speed Increase' },
        { metric: '40%', label: 'Bug Reduction' }
      ],
      achievements: [
        'Built large-scale, reusable React & TypeScript components, reducing development redundancy by 60%',
        'Delivered pixel-perfect, interactive UIs for Walgreens Boots that simplified workflows',
        'Engineered scalable micro-frontend architecture with secure dynamic routing for Maersk',
        'Implemented CI/CD pipelines, mentoring peers, and enforcing best practices'
      ],
      skills: ['React', 'TypeScript', 'Micro-Frontend', 'Node.js', 'CI/CD', 'Performance Optimization']
    },
    {
      date: 'May 2021 – Jul 2021',
      title: 'Frontend Developer Mobile-React Native',
      company: 'Steady Rabbit',
      location: 'Remote',
      icon: '🐰',
      logo: steadyrabbitLogo,
      color: '#0A84FF',
      duration: '3 months',
      highlights: [
        { metric: '2x', label: 'User Engagement' },
        { metric: '95%', label: 'Crash-Free Sessions' },
        { metric: '100%', label: 'Cross-Platform' },
        { metric: '35%', label: 'Performance Score Increase' },
        { metric: '50%', label: 'Load Time Reduction' }
      ],
      achievements: [
        'Developed interactive hybrid mobile app features including dark mode and theme switching',
        'Integrated backend APIs to optimize data flow and app reliability',
        'Enhanced mobile user experience through responsive design'
      ],
      skills: ['React Native', 'Mobile Dev', 'API Integration', 'Responsive Design', 'Dark Mode']
    },
    {
      date: 'Jul 2019 – Dec 2019',
      title: 'Mobile Application Developer (Intern)',
      company: 'Arawinz Soft Solutions',
      location: 'Hyderabad, TG, India · On-site',
      icon: '🚀',
      logo: arawinzLogo,
      color: '#0A84FF',
      duration: '6 months',
      highlights: [
        { metric: '2', label: 'Platforms Supported' },
        { metric: '100%', label: 'Mobile Compatible' },
        { metric: '1', label: 'Published App' },
        { metric: '30%', label: 'User Engagement Increase' },
        { metric: '25%', label: 'Performance Improvement' }
      ],
      achievements: [
        'Developed cross-platform mobile features for the "ESAY" app using React Native',
        'Contributed to company website development using HTML, CSS, and JavaScript',
        'Enhanced design responsiveness and mobile compatibility'
      ],
      skills: ['React Native', 'HTML/CSS', 'JavaScript', 'Cross-Platform', 'UI Development']
    }
  ];

  const calculateTotalExperience = () => {
    const startDate = new Date('2019-07-01');
    const endDate = new Date('2023-06-30');
    const years = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24 * 365));
    return `${years}+ Years`;
  };

  // Handle card expansion and scroll on mobile/tablet
  const handleCardClick = (index) => {
    const newExpandedIndex = expandedIndex === index ? -1 : index;
    setExpandedIndex(newExpandedIndex);

    // Scroll to expanded card on mobile/tablet
    if (newExpandedIndex !== -1 && isMobile && cardRefs.current[newExpandedIndex]) {
      setTimeout(() => {
        cardRefs.current[newExpandedIndex].scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });
      }, 100); // Small delay to allow expansion animation to start
    }
  };

  // Update mobile detection on resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section id="experience" className="experience-section">
      <div className="experience-container-inner">
        <div className="section-header">
          <h2 className="section-title">Professional Experience</h2>
          <p className="section-subtitle">
            A journey through impactful projects, collaborative teams, and continuous growth
          </p>
          <div className="experience-stats">
            <div className="exp-stat-item">
              <span className="exp-stat-value">{calculateTotalExperience()}</span>
              <span className="exp-stat-label">Total Experience</span>
            </div>
            <div className="stat-divider"></div>
            <div className="exp-stat-item">
              <span className="exp-stat-value">3</span>
              <span className="exp-stat-label">Companies</span>
            </div>
            <div className="stat-divider"></div>
            <div className="exp-stat-item">
              <span className="exp-stat-value">5+</span>
              <span className="exp-stat-label">Major Projects</span>
            </div>
          </div>
        </div>

        <div className="experience-timeline-enhanced">
          {experiences.map((exp, index) => (
            <div 
              key={index} 
              ref={el => cardRefs.current[index] = el}
              className={`timeline-card ${expandedIndex === index ? 'expanded' : ''}`}
              onClick={() => handleCardClick(index)}
            >
              {/* Timeline connector */}
              <div className="timeline-connector">
                <div className="connector-line"></div>
                <div className="connector-dot" style={{ borderColor: exp.color }}>
                  {exp.logo ? (
                    <img src={exp.logo} alt={`${exp.company} logo`} className="connector-icon connector-logo" />
                  ) : (
                    <span className="connector-icon">{exp.icon}</span>
                  )}
                </div>
                {index < experiences.length - 1 && <div className="connector-line-bottom"></div>}
              </div>

              {/* Card content */}
              <div className="timeline-card-content">
                {/* Header - always visible */}
                <div className="card-header">
                  <div className="card-header-left">
                    <span className="card-duration-badge" style={{ background: exp.color }}>
                      {exp.duration}
                    </span>
                    <h3 className="card-title">{exp.title}</h3>
                    <div className="card-company">
                      <span className="company-name">{exp.company}</span>
                      <span className="company-location">{exp.location}</span>
                    </div>
                  </div>
                  <div className="card-header-right">
                    <span className="card-date">{exp.date}</span>
                    <div className={`expand-indicator ${expandedIndex === index ? 'rotated' : ''}`}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6,9 12,15 18,9"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Metrics bar - always visible */}
                <div className="card-metrics">
                  {exp.highlights.map((highlight, hIdx) => (
                    <div key={hIdx} className="metric-item">
                      <span className="metric-value" style={{ color: exp.color }}>{highlight.metric}</span>
                      <span className="metric-label">{highlight.label}</span>
                    </div>
                  ))}
                </div>

                {/* Expandable content */}
                <div className="card-expandable">
                  <div className="card-achievements">
                    <h4>Key Achievements</h4>
                    <ul>
                      {exp.achievements.map((achievement, aIdx) => (
                        <li key={aIdx}>
                          <span className="achievement-bullet" style={{ background: exp.color }}></span>
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="card-skills">
                    <h4>Technologies & Skills</h4>
                    <div className="skills-grid">
                      {exp.skills.map((skill, sIdx) => (
                        <span 
                          key={sIdx} 
                          className={`skill-chip ${hoveredSkill === `${index}-${sIdx}` ? 'hovered' : ''}`}
                          style={{ 
                            '--skill-color': exp.color,
                            animationDelay: `${sIdx * 0.05}s`
                          }}
                          onMouseEnter={() => setHoveredSkill(`${index}-${sIdx}`)}
                          onMouseLeave={() => setHoveredSkill(null)}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Progress bar at bottom */}
                <div className="card-progress">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: expandedIndex === index ? '100%' : '0%',
                      background: `linear-gradient(90deg, ${exp.color}, ${exp.color}88)`
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
